import { useState, useEffect, useCallback, useRef } from 'react';
import { Hourglass, Clock } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

// Configuration (seconds)
const IDLE_WARNING_SEC = 270;   // 4:30 – show warning modal
const IDLE_LOGOUT_SEC  = 300;   // 5:00 – auto-logout
const TICK_MS          = 1000;
const ACTIVITY_EVENTS  = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
const PUBLIC_PATHS     = ['/login', '/register', '/forgot-password', '/reset-password', '/portal/register'];

export default function IdleTimeoutManager() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [secondsLeft, setSecondsLeft] = useState(IDLE_LOGOUT_SEC);
  const [warningOpen, setWarningOpen] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const loggingOutRef = useRef(false);

  const isPublicRoute = PUBLIC_PATHS.some(p => location.pathname.startsWith(p));
  const isActive = !!user && !isPublicRoute;

  const resetIdle = useCallback(() => {
    lastActivityRef.current = Date.now();
    setSecondsLeft(IDLE_LOGOUT_SEC);
    if (warningOpen) setWarningOpen(false);
  }, [warningOpen]);

  const performLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try {
      await logout();
    } catch {
      // ignore network failures – we still navigate to login
    } finally {
      const target = location.pathname.startsWith('/portal') ? '/login' : '/login';
      navigate(target, { replace: true, state: { reason: 'idle_timeout' } });
      loggingOutRef.current = false;
    }
  }, [logout, navigate, location.pathname]);

  // Attach activity listeners (do NOT reset while warning open – user must click button)
  useEffect(() => {
    if (!isActive) return;
    const handler = () => {
      if (warningOpen) return;
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, handler, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, handler));
  }, [isActive, warningOpen]);

  // Tick: check idle delta every second
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      const idleSec = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, IDLE_LOGOUT_SEC - idleSec);
      setSecondsLeft(remaining);

      if (idleSec >= IDLE_LOGOUT_SEC) {
        performLogout();
      } else if (idleSec >= IDLE_WARNING_SEC && !warningOpen) {
        setWarningOpen(true);
      }
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [isActive, warningOpen, performLogout]);

  // Reset when route changes (e.g. fresh login)
  useEffect(() => {
    lastActivityRef.current = Date.now();
    setSecondsLeft(IDLE_LOGOUT_SEC);
    setWarningOpen(false);
  }, [user?.id, location.pathname]);

  if (!isActive || !warningOpen) return null;

  const mm = Math.floor(secondsLeft / 60);
  const ss = (secondsLeft % 60).toString().padStart(2, '0');

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      data-testid="idle-timeout-modal"
      role="alertdialog"
      aria-modal="true"
    >
      <div className="bg-white max-w-md w-full rounded-lg shadow-xl border-2 border-amber-400">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-amber-50">
          <Hourglass weight="fill" className="w-7 h-7 text-amber-600" />
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">Inaktivität erkannt</h2>
            <p className="text-xs text-slate-500">Aus Sicherheitsgründen werden Sie bald abgemeldet.</p>
          </div>
        </div>
        <div className="p-6 space-y-4 text-center">
          <p className="text-sm text-slate-700">
            Sie werden in <span className="font-mono font-black text-amber-700 text-2xl" data-testid="idle-countdown">{mm}:{ss}</span> automatisch abgemeldet.
          </p>
          <p className="text-sm text-slate-700">
            Möchten Sie angemeldet bleiben?
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
            <Clock weight="bold" className="w-3.5 h-3.5" />
            <span>Letzte Aktivität: vor {Math.floor((Date.now() - lastActivityRef.current) / 1000)}s</span>
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-3">
          <button
            type="button"
            onClick={performLogout}
            className="flex-1 border border-slate-300 text-slate-700 py-2.5 font-medium hover:bg-slate-50 rounded"
            data-testid="idle-logout-now-button"
          >
            Jetzt abmelden
          </button>
          <button
            type="button"
            onClick={resetIdle}
            className="flex-1 bg-emerald-600 text-white py-2.5 font-medium hover:bg-emerald-700 rounded"
            data-testid="idle-stay-logged-in-button"
          >
            Angemeldet bleiben
          </button>
        </div>
      </div>
    </div>
  );
}
